import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { toast } from 'react-toastify';

import type { AppfolioConfig, PmsType } from '../../types';
import type { Unit } from '@/Units/types';
import { updateUnitPmsConfig } from '@/Units/api';

import { AppfolioMatchingDialog } from './AppfolioMatchingDialog';
import { useBillableUnits } from './pmsUtils';

const useStyles = makeStyles(() => ({
  fieldLabel: {
    fontWeight: 600,
    marginBottom: '4px',
  },
  fieldValue: { color: '#666' },
  placeholder: { color: '#999', fontStyle: 'italic' },
}));

interface AppfolioConfigFormProps {
  pmsConfig: AppfolioConfig | null;
  units: Unit[];
  onUpdate: (pmsType: PmsType | null, pmsConfig: AppfolioConfig | null) => Promise<void>;
  onRefresh?: () => void;
  /** When provided AND the form is in pre-save state (no pmsConfig), the unconfigured
   * view shows a Cancel button next to "Upload AppFolio Export". Used by PmsSettings
   * to let the user back out of an AppFolio selection without first saving anything,
   * so they can pick a different PMS provider. */
  onCancel?: () => void;
}

export const AppfolioConfigForm = ({
  pmsConfig,
  units,
  onUpdate,
  onRefresh,
  onCancel,
}: AppfolioConfigFormProps): JSX.Element => {
  const classes = useStyles();
  const [showMatching, setShowMatching] = React.useState(false);
  const [showClearConfirm, setShowClearConfirm] = React.useState(false);
  const [isClearing, setIsClearing] = React.useState(false);

  const billableUnits = useBillableUnits(units);

  const mappedCount = React.useMemo(
    () => billableUnits.filter((u) => u.pms_config?.unit_id != null).length,
    [billableUnits],
  );

  const handleClearPms = async () => {
    setIsClearing(true);
    try {
      await onUpdate(null, null);
      await Promise.all(
        billableUnits.filter((u) => u.pms_config).map((u) => updateUnitPmsConfig(u.id, null, null)),
      );
      onRefresh?.();
      setShowClearConfirm(false);
      toast.success('PMS settings cleared', { closeButton: true });
    } catch {
      toast.error('Failed to clear PMS settings', { closeButton: true });
    } finally {
      setIsClearing(false);
    }
  };

  // Unconfigured: single CTA
  if (!pmsConfig) {
    return (
      <Card sx={{ mt: 2, mb: 8 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            AppFolio Integration
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Upload your AppFolio Charges Import template to map your SimpleSub units to AppFolio
            units. Once mapped, you&apos;ll be able to download an AppFolio-ready CSV from any
            billing breakdown.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {onCancel && (
              <Button variant="outlined" size="small" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button variant="contained" size="small" onClick={() => setShowMatching(true)}>
              Upload AppFolio Export
            </Button>
          </Box>
        </CardContent>
        <AppfolioMatchingDialog
          open={showMatching}
          onClose={() => setShowMatching(false)}
          units={units}
          onUpdate={onUpdate}
          onRefresh={onRefresh}
          existingConfig={null}
        />
      </Card>
    );
  }

  // Configured: summary view
  return (
    <Card sx={{ mt: 2, mb: 8 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          AppFolio Integration
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Property Name
          </Typography>
          <Typography className={classes.fieldValue}>{pmsConfig.property_name}</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Unit Mappings
          </Typography>
          <Typography className={classes.fieldValue}>
            {mappedCount} of {billableUnits.length} units mapped
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {mappedCount > 0 ? (
            <Chip label="CSV Ready" color="success" size="small" variant="outlined" />
          ) : (
            <Tooltip title="At least one unit mapping is required to export CSV" arrow>
              <Chip label="CSV Not Ready" color="default" size="small" variant="outlined" />
            </Tooltip>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Re-upload after tenant turnover.</strong> Tenant and occupancy info is captured
          when you upload the AppFolio template. If a tenant moves in or out, the next export will
          post charges against the old occupancy until you click &quot;Reconfigure AppFolio&quot;
          and upload a fresh template.
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={() => setShowClearConfirm(true)}
            disabled={isClearing}
          >
            Clear PMS
          </Button>
          <Button variant="contained" size="small" onClick={() => setShowMatching(true)}>
            Reconfigure AppFolio
          </Button>
        </Box>
      </CardContent>

      <Dialog open={showClearConfirm} onClose={() => !isClearing && setShowClearConfirm(false)}>
        <DialogTitle>Clear PMS Settings?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove the AppFolio configuration and all unit mappings for this property. You
            can reconfigure at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearConfirm(false)} disabled={isClearing}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleClearPms} disabled={isClearing}>
            {isClearing ? <CircularProgress size={16} /> : 'Clear PMS'}
          </Button>
        </DialogActions>
      </Dialog>

      <AppfolioMatchingDialog
        open={showMatching}
        onClose={() => setShowMatching(false)}
        units={units}
        onUpdate={onUpdate}
        onRefresh={onRefresh}
        existingConfig={pmsConfig}
      />
    </Card>
  );
};
